import React, { useEffect, useContext, useCallback } from 'react';
import { COMPETENCE_ORDERS_LIST_QUERY } from '../../queries';
import { COMPETENCE_ORDER_DELETE_SUBSCRIPTION } from '../../subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import CompetenceOrderItem from './CompetenceOrderItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { COMPETENCE_ORDERS_LIST_DELETE } from 'features/competenceOrder/mutations';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import useSubscription from 'hooks/apollo/useSubscription';

export const competenceOrderStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'approvalTypeName',
      caption: 'CompetenceOrders.ApprovalTypeName',
      type: 'text'
    },
    {
      name: 'statusName',
      caption: 'CompetenceOrders.StatusName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'CompetenceOrders.ApprovalTypeName',
      value: 'ApprovalTypeName'
    },
    {
      caption: 'CompetenceOrders.StatusName',
      value: 'StatusName'
    }
  ],
  pager: {
    orderBy: 'ApprovalTypeName',
    direction: 'asc'
  }
});

const CompetenceOrderListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(competenceOrderStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: COMPETENCE_ORDERS_LIST_QUERY,
        name: 'competenceOrders'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(COMPETENCE_ORDER_DELETE_SUBSCRIPTION, {
    onData: _ => {
      addToast(t('CompetenceOrders.DeletedCompetenceOrder'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'competenceOrders' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'competenceOrder' });
      client.cache.gc();
    }
  });

  const [deleteCompetenceOrders] = useMutation(COMPETENCE_ORDERS_LIST_DELETE);

  const handleDelete = useCallback(
    competenceOrderId => {
      confirm(
        () => {
          deleteCompetenceOrders({ variables: { input: { competenceOrderId } } });
        },
        {
          content: t('CompetenceOrders.DeleteRule')
        }
      );
    },
    [deleteCompetenceOrders, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'competenceOrders' });
      client.cache.gc();
    }
  }, [activeService, client]);

  // TODO Wrong way to add actions to list-browser!! Will need a refactor
  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions, t]);

  const itemComponent = useCallback(({ data }) => <CompetenceOrderItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'approvalTypeName', caption: 'CompetenceOrders.ApprovalTypeName' },
      { name: 'statusName', caption: 'CompetenceOrders.StatusName' },
      { name: 'actions', caption: 'Actions' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='competence-order-browser' store={competenceOrderStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={competenceOrderStore} options={fetchingOptions} />
    </Card>
  );
};

export default CompetenceOrderListBrowser;
